import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../text/Text';

let WebView = null;
if (Platform.OS !== 'web') {
	const { WebView: ReactNativeWebView } = require('react-native-webview');
	WebView = ReactNativeWebView;
}

const PAD_HEIGHT = 180;

// Inline canvas page for the native WebView: draws touch strokes and posts the PNG
// data URL back after every stroke so the parent always holds the latest signature.
const buildSignaturePadHtml = () => `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
<style>
	html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; background-color: transparent; }
	canvas { display: block; width: 100%; height: 100%; touch-action: none; }
</style>
</head>
<body>
<canvas id="pad"></canvas>
<script>
	var canvas = document.getElementById('pad');
	var context = canvas.getContext('2d');
	var drawing = false;
	var hasInk = false;

	function resizePad () {
		var ratio = window.devicePixelRatio || 1;
		canvas.width = canvas.clientWidth * ratio;
		canvas.height = canvas.clientHeight * ratio;
		context.scale(ratio, ratio);
		context.lineWidth = 2;
		context.lineCap = 'round';
		context.lineJoin = 'round';
		context.strokeStyle = '#1c2127';
	}

	function pointFromEvent (event) {
		var source = event.touches && event.touches.length ? event.touches[0] : event;
		var rect = canvas.getBoundingClientRect();
		return { x: source.clientX - rect.left, y: source.clientY - rect.top };
	}

	function startStroke (event) {
		event.preventDefault();
		drawing = true;
		var point = pointFromEvent(event);
		context.beginPath();
		context.moveTo(point.x, point.y);
	}

	function continueStroke (event) {
		if (!drawing) { return; }
		event.preventDefault();
		var point = pointFromEvent(event);
		context.lineTo(point.x, point.y);
		context.stroke();
		hasInk = true;
	}

	function endStroke (event) {
		if (!drawing) { return; }
		event.preventDefault();
		drawing = false;
		if (hasInk && window.ReactNativeWebView) {
			window.ReactNativeWebView.postMessage(canvas.toDataURL('image/png'));
		}
	}

	function clearPad () {
		context.clearRect(0, 0, canvas.width, canvas.height);
		hasInk = false;
	}

	canvas.addEventListener('touchstart', startStroke, { passive: false });
	canvas.addEventListener('touchmove', continueStroke, { passive: false });
	canvas.addEventListener('touchend', endStroke, { passive: false });
	canvas.addEventListener('mousedown', startStroke);
	canvas.addEventListener('mousemove', continueStroke);
	canvas.addEventListener('mouseup', endStroke);
	window.clearPad = clearPad;
	resizePad();
</script>
</body>
</html>`;

const WebCanvasSignaturePad = forwardRef(({ onChange }, ref) => {
	const canvasRef = useRef(null);
	const drawingRef = useRef(false);
	const hasInkRef = useRef(false);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const ratio = window.devicePixelRatio || 1;
		canvas.width = canvas.clientWidth * ratio;
		canvas.height = canvas.clientHeight * ratio;
		const context = canvas.getContext('2d');
		context.scale(ratio, ratio);
		context.lineWidth = 2;
		context.lineCap = 'round';
		context.lineJoin = 'round';
		context.strokeStyle = '#1c2127';
	}, []);

	useImperativeHandle(ref, () => ({
		clear: () => {
			const canvas = canvasRef.current;
			if (canvas) {
				canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
			}
			hasInkRef.current = false;
		},
	}), []);

	const pointFromEvent = useCallback((event) => {
		const rect = canvasRef.current.getBoundingClientRect();
		return { x: event.clientX - rect.left, y: event.clientY - rect.top };
	}, []);

	const startStroke = useCallback((event) => {
		drawingRef.current = true;
		event.target.setPointerCapture?.(event.pointerId);
		const context = canvasRef.current.getContext('2d');
		const point = pointFromEvent(event);
		context.beginPath();
		context.moveTo(point.x, point.y);
	}, [pointFromEvent]);

	const continueStroke = useCallback((event) => {
		if (!drawingRef.current) {
			return;
		}

		const context = canvasRef.current.getContext('2d');
		const point = pointFromEvent(event);
		context.lineTo(point.x, point.y);
		context.stroke();
		hasInkRef.current = true;
	}, [pointFromEvent]);

	const endStroke = useCallback(() => {
		if (!drawingRef.current) {
			return;
		}

		drawingRef.current = false;
		if (hasInkRef.current) {
			onChange(canvasRef.current.toDataURL('image/png'));
		}
	}, [onChange]);

	return (
		<canvas
			ref={canvasRef}
			onPointerDown={startStroke}
			onPointerMove={continueStroke}
			onPointerUp={endStroke}
			onPointerLeave={endStroke}
			style={{
				width: '100%',
				height: '100%',
				touchAction: 'none',
				display: 'block',
			}}
		/>
	);
});

// Drawn-signature input for company document acknowledgements. Calls onChange with a PNG
// data URL after every stroke, and with '' when cleared.
const SignaturePad = ({ value, onChange, style }) => {
	const styles = useThemedStyles(createStyles);
	const webViewRef = useRef(null);
	const webCanvasRef = useRef(null);

	const onWebViewMessage = useCallback((event) => {
		const dataUrl = event?.nativeEvent?.data || '';
		if (dataUrl.startsWith('data:image/png')) {
			onChange(dataUrl);
		}
	}, [onChange]);

	const clearPad = useCallback(() => {
		if (Platform.OS === 'web') {
			webCanvasRef.current?.clear();
		}
		else {
			webViewRef.current?.injectJavaScript('window.clearPad(); true;');
		}

		onChange('');
	}, [onChange]);

	return (
		<View style={style}>
			<View style={styles.padContainer}>
				{
					Platform.OS === 'web' ? (
						<WebCanvasSignaturePad ref={webCanvasRef} onChange={onChange} />
					) : (
						<WebView
							ref={webViewRef}
							source={{ html: buildSignaturePadHtml() }}
							originWhitelist={['*']}
							onMessage={onWebViewMessage}
							scrollEnabled={false}
							javaScriptEnabled={true}
							style={styles.webView}
						/>
					)
				}
			</View>
			<View style={styles.footerRow}>
				<Text style={styles.hint}>
					{value ? 'Signature captured' : 'Sign in the box above'}
				</Text>
				<TouchableOpacity onPress={clearPad}>
					<Text style={styles.clearText}>Clear</Text>
				</TouchableOpacity>
			</View>
		</View>
	);
};

const createStyles = (theme) => ({
	padContainer: {
		height: PAD_HEIGHT,
		borderWidth: 1,
		borderColor: theme.border1,
		borderRadius: 8,
		backgroundColor: '#ffffff',
		overflow: 'hidden',
	},
	webView: {
		flex: 1,
		backgroundColor: 'transparent',
	},
	footerRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: 6,
	},
	hint: {
		color: theme.text3,
		fontSize: 12,
	},
	clearText: {
		color: theme.primary1,
		fontSize: 13,
		fontWeight: '600',
	},
});

export default SignaturePad;
